import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { formatDistanceToNow } from 'date-fns'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { TagLabel } from '@/components/ui/tag-label'
import { CircleNumber } from '@/components/ui/circle-number'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  ArrowLeft,
  SquareArrowOutUpRight,
  Ban,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Zap,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  CircleMinus,
} from 'lucide-react'
import { OPERATORS } from '@/types/filter'
import { getActionTypeMetadata } from '@/types/action'

interface ShowProps {
  account: {
    id: number
    name: string
    gmailSessionId: number
  }
  email: {
    id: number
    fromName: string | null
    fromEmail: string | null
    subject: string | null
    receivedAt: string
    gmailMessageId: string
    snippet: string | null
    statusOnEntry: Array<{ id: string; name: string }>
    categoriesOnEntry: Array<{ id: string; name: string }>
    labelsOnEntry: Array<{ id: string; name: string }>
    processingDuration: number | null
  }
  senderBlocked: boolean
  workflowResults: Array<{
    id: number
    workflow: {
      id: number | null
      name: string
      priority: number | null
      isActive: boolean
      deleted?: boolean
    }
    status: string
    startedAt: string | null
    completedAt: string | null
    durationMs: number | null
    filters: Array<{
      filter_id: number
      filter_name: string
      passed?: boolean
      skipped?: boolean
      evaluated_at?: string
      details: {
        operator?: string
        conditions?: Array<{
          field: string
          operator: string
          expected_value: string
          actual_value: any
          matched: boolean
        }>
        condition_groups?: Array<{
          operator: string
          conditions: Array<{
            field: string
            operator: string
            expected_value: string
            actual_value: any
            matched: boolean
          }>
          passed: boolean
        }>
        failed_condition?: {
          field: string
          operator: string
          expected_value: string
          actual_value: any
          matched: boolean
        }
      }
    }>
    actions: Array<{
      action_id: number
      action_order: number
      action_type: string
      success: boolean
      executed_at: string
      details: Record<string, any>
    }>
  }>
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
    timezone?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

export default function Show({
  account,
  email,
  senderBlocked,
  workflowResults,
  currentUser,
  gmailConnection,
}: ShowProps) {
  const [blocked, setBlocked] = useState(senderBlocked)
  const [showBlockDialog, setShowBlockDialog] = useState(false)
  const [showUnblockDialog, setShowUnblockDialog] = useState(false)
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<number>>(new Set())

  const gmailUrl = `https://mail.google.com/mail/u/${account.gmailSessionId}/#all/${email.gmailMessageId}`
  const receivedTime = formatDistanceToNow(new Date(email.receivedAt), { addSuffix: true })
  const fullDate = new Date(email.receivedAt).toLocaleString()

  const workflowsPassed = workflowResults.filter((r) => r.status === 'success').length
  const workflowsTotal = workflowResults.length

  const handleToggleBlocked = () => {
    if (blocked) {
      setShowUnblockDialog(true)
    } else {
      setShowBlockDialog(true)
    }
  }

  const confirmToggleBlocked = async () => {
    try {
      const response = await fetch(`/accounts/${account.id}/activity/${email.id}/toggle_sender_blocked`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        credentials: 'same-origin',
      })
      const data = await response.json()
      setBlocked(data.blocked)
      setShowBlockDialog(false)
      setShowUnblockDialog(false)
    } catch (error) {
      console.error('Failed to toggle blocked status:', error)
    }
  }

  const getOperatorLabel = (operator: string): string => {
    return OPERATORS[operator as keyof typeof OPERATORS]?.label || operator
  }

  const getActionTypeLabel = (actionType: string): string => {
    const metadata = getActionTypeMetadata(actionType as any)
    return metadata?.label || actionType
  }

  const formatActualValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    if (value === null || value === undefined) {
      return '(empty)'
    }
    return String(value)
  }

  const toggleWorkflow = (workflowId: number) => {
    setExpandedWorkflows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(workflowId)) {
        newSet.delete(workflowId)
      } else {
        newSet.add(workflowId)
      }
      return newSet
    })
  }

  const getFiltersPassedCount = (filters: typeof workflowResults[0]['filters']): number => {
    return filters.filter((f) => !f.skipped && f.passed).length
  }

  return (
    <>
      <Head title={email.subject || '(No subject)'} />
      <DashboardLayout
        currentUser={currentUser}
        gmailConnection={gmailConnection}
        account={account}
        activeTab="activity"
      >
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.get(`/accounts/${account.id}/activity`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Activity
          </Button>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Yellow div: Subject + Sender */}
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
                {email.subject || '(No subject)'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {email.fromName && email.fromEmail
                  ? `${email.fromName} <${email.fromEmail}>`
                  : email.fromEmail || email.fromName || 'Unknown'}
              </p>
            </div>

            {/* Green div: Date + Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 lg:flex-col lg:items-end lg:gap-4">
              <div className="flex flex-row gap-2">
                <Button
                  onClick={() => window.open(gmailUrl, '_blank')}
                  className="w-auto"
                  iconRight={<SquareArrowOutUpRight className="w-3.5 h-3.5" />}
                >
                  View in Gmail
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleToggleBlocked}
                  className="w-auto"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {blocked ? 'Unblock Sender' : 'Block Sender'}
                </Button>
              </div>
              <p className="text-gray-600 dark:text-gray-400 whitespace-nowrap">
                {fullDate}
              </p>
            </div>
          </div>
        </div>

        {/* Email Content */}
        <Card className="bg-white">
          <CardContent className="space-y-4 p-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-24">
              {email.statusOnEntry.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status on Entry
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {email.statusOnEntry.map((status) => (
                      <TagLabel key={status.id}>
                        {status.name}
                      </TagLabel>
                    ))}
                  </div>
                </div>
              )}

              {email.categoriesOnEntry.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Categorized
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {email.categoriesOnEntry.map((category) => (
                      <TagLabel key={category.id}>
                        {category.name}
                      </TagLabel>
                    ))}
                  </div>
                </div>
              )}

              {email.labelsOnEntry.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Labels on Entry
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {email.labelsOnEntry.map((label) => (
                      <TagLabel key={label.id}>
                        {label.name}
                      </TagLabel>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {email.snippet && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Content Snippet
                </label>
                <p className="text-blue-950 dark:text-white whitespace-pre-wrap">
                  {email.snippet}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Workflow Processing Results */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-gray-500 dark:text-gray-400">
              Workflow Processing ({workflowsPassed} of {workflowsTotal} workflows passed)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {workflowResults.map((result, index) => {
              const isSuccess = result.status === 'success'
              const isDeleted = result.workflow.deleted
              const isExpanded = expandedWorkflows.has(result.id)
              const filtersPassed = getFiltersPassedCount(result.filters)
              const totalFilters = result.filters.length
              const actionsTaken = result.actions.length
              const priorityNumber = result.workflow.priority !== null ? result.workflow.priority : index + 1

              return (
                <div
                  key={result.id}
                  className="border rounded-lg border-gray-200 dark:!border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                >
                  {/* Collapsible Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-opacity-70 transition-colors"
                    onClick={() => toggleWorkflow(result.id)}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0 w-full sm:w-auto">
                        {/* Circle Number */}
                        <CircleNumber
                          number={priorityNumber}
                          bgColor="bg-white dark:bg-gray-700"
                          textColor="text-gray-700 dark:text-gray-300"
                          className="border border-gray-300 dark:!border-gray-700 flex-shrink-0"
                        />

                        {/* Status Icon */}
                        {isSuccess ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                        )}

                        {/* Workflow Name */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-blue-950 dark:text-white truncate">
                            {result.workflow.name}
                          </h3>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-500">
                          <span>{filtersPassed} of {totalFilters} filters passed</span>
                          {isSuccess && (
                            <span>{actionsTaken} action{actionsTaken !== 1 ? 's' : ''} taken</span>
                          )}
                        </div>

                        {/* Status Label */}
                        <TagLabel
                          textColor={isSuccess ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}
                          borderColor={isSuccess ? "border-green-600 dark:border-green-400" : "border-red-600 dark:border-red-400"}
                          className={`border px-1.5 py-1 rounded-sm flex-shrink-0 bg-white dark:bg-gray-700 ${isSuccess ? "!border-green-600 dark:!border-green-400" : "!border-red-600 dark:!border-red-400"}`}
                          leftIcon={isSuccess ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        >
                          {isSuccess ? 'Workflow Passed' : 'Workflow Failed'}
                        </TagLabel>

                        {/* Chevron Icon */}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-opacity-20 border-gray-300 dark:!border-gray-700">
                      {isDeleted && (
                        <div className="mt-4 mb-4 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            ⚠️ This workflow has been deleted
                          </p>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 mb-4">
                        {result.startedAt && (() => {
                          const timezone = currentUser?.timezone || 'America/New_York'
                          const date = new Date(result.startedAt)
                          const now = new Date()
                          const currentYear = now.getFullYear()
                          const dateYear = date.getFullYear()

                          // Format date in the specified timezone
                          const formatter = new Intl.DateTimeFormat('en-US', {
                            timeZone: timezone,
                            month: 'short',
                            day: 'numeric',
                            year: dateYear < currentYear ? 'numeric' : undefined,
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })

                          const parts = formatter.formatToParts(date)
                          const month = parts.find(p => p.type === 'month')?.value || ''
                          const day = parts.find(p => p.type === 'day')?.value || ''
                          const year = parts.find(p => p.type === 'year')?.value
                          const hour = parts.find(p => p.type === 'hour')?.value || ''
                          const minute = parts.find(p => p.type === 'minute')?.value || ''
                          const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value || ''

                          // Get timezone abbreviation
                          const tzFormatter = new Intl.DateTimeFormat('en-US', {
                            timeZone: timezone,
                            timeZoneName: 'short',
                          })
                          const tzParts = tzFormatter.formatToParts(date)
                          const tzAbbr = tzParts.find(p => p.type === 'timeZoneName')?.value || 'EST'

                          // Format hour:minute with AM/PM (12-hour format)
                          const formattedTime = `${hour}:${minute.padStart(2, '0')} ${dayPeriod.toUpperCase()}`

                          const formattedDate = year && parseInt(year) < currentYear
                            ? `${month} ${day}, ${year} ⋅ ${formattedTime} ${tzAbbr}`
                            : `${month} ${day} ⋅ ${formattedTime} ${tzAbbr}`

                          return (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Evaluated {formattedDate}
                            </p>
                          )
                        })()}
                        {!isDeleted && result.workflow.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              router.get(`/accounts/${account.id}/workflows/${result.workflow.id}`)
                            }}
                            className="w-auto text-gray-600 dark:text-gray-400"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit workflow
                          </Button>
                        )}
                      </div>

                      {/* Filter Steps */}
                      {result.filters.map((filter, filterIndex) => {
                        const filterOperator = filter.details?.operator || 'AND'
                        const hasDirectConditions = filter.details?.conditions && filter.details.conditions.length > 0
                        const hasConditionGroups = filter.details?.condition_groups && filter.details.condition_groups.length > 0
                        const totalItems = (hasDirectConditions ? 1 : 0) + (hasConditionGroups ? filter.details.condition_groups.length : 0)

                        return (
                          <div key={filter.filter_id}>
                            <div className="mb-12">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                  <Filter className="w-4 h-4 text-gray-500 hidden sm:block" />
                                  <h4 className="font-medium text-blue-950 dark:text-white">
                                    {filter.filter_name || `Filter Steps (${filterIndex + 1})`}
                                  </h4>
                                </div>
                                <div className="flex-shrink-0">
                                  {filter.skipped ? (
                                    <TagLabel
                                      textColor="text-gray-600 dark:text-gray-400"
                                      borderColor="border-gray-400 dark:border-gray-500"
                                      className="inline-flex border px-1.5 py-1 rounded-sm bg-white dark:bg-gray-700 !border-gray-400 dark:!border-gray-500"
                                      leftIcon={<CircleMinus className="w-4 h-4" />}
                                    >
                                      Skipped
                                    </TagLabel>
                                  ) : (
                                    <TagLabel
                                      textColor={filter.passed ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}
                                      borderColor={filter.passed ? "border-green-600 dark:border-green-400" : "border-red-600 dark:border-red-400"}
                                      className={`inline-flex border px-1.5 py-1 rounded-sm bg-white dark:bg-gray-700 ${filter.passed ? "!border-green-600 dark:!border-green-400" : "!border-red-600 dark:!border-red-400"}`}
                                      leftIcon={filter.passed ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                                    >
                                      {filter.passed ? 'Passed' : 'Failed'}
                                    </TagLabel>
                                  )}
                                </div>
                              </div>

                              <div className="ml-0 sm:ml-6 space-y-3">
                                {/* Main Conditions */}
                                {filter.details?.conditions?.map((condition, condIndex) => {
                                  const isFailed = !condition.matched && filter.details?.failed_condition?.field === condition.field
                                  const isSkipped = filter.skipped

                                  return (
                                    <div key={condIndex}>
                                      {/* Show "and" or "or" label between conditions */}
                                      {condIndex > 0 && (
                                        <div className="flex items-center justify-center my-2">
                                          <span className="px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:!border-gray-700">
                                            {filterOperator}
                                          </span>
                                        </div>
                                      )}
                                      <div
                                        className={`p-3 rounded border ${
                                          isSkipped
                                            ? '!border-gray-300 dark:!border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                                            : condition.matched
                                            ? '!border-green-300 dark:!border-green-700 bg-green-100/70 dark:bg-green-900/30'
                                            : '!border-red-300 dark:!border-red-700 bg-red-100/70 dark:bg-red-900/30'
                                        }`}
                                      >
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                          <div className="flex items-start gap-2 flex-1 min-w-0">
                                            {isSkipped ? (
                                              <CircleMinus className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                                            ) : condition.matched ? (
                                              <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                            ) : (
                                              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium text-blue-950 dark:text-white">
                                                {condition.field} {getOperatorLabel(condition.operator)} "{condition.expected_value}"
                                              </p>
                                              {!isSkipped && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                  Actual value: {formatActualValue(condition.actual_value)}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          {!isSkipped && (
                                            <TagLabel
                                              textColor={condition.matched ? "text-green-600 dark:text-green-600" : "text-red-600 dark:text-red-600"}
                                              className={`inline-flex border px-1.5 py-1 rounded-sm bg-transparent ${condition.matched ? "!border-green-600 dark:!border-green-600" : "!border-red-600 dark:!border-red-600"}`}
                                              leftIcon={condition.matched ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                            >
                                              {condition.matched ? 'Condition matched' : 'Condition did not match'}
                                            </TagLabel>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}

                                {/* Condition Groups */}
                                {filter.details?.condition_groups?.map((group, groupIndex) => {
                                  const isSkipped = filter.skipped

                                  return (
                                    <div key={groupIndex}>
                                      {/* Show "and" or "or" label before first group if there are direct conditions, or between groups */}
                                      {(groupIndex === 0 && hasDirectConditions) || groupIndex > 0 ? (
                                        <div className="flex items-center justify-center my-2">
                                          <span className="px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-800">
                                            {filterOperator}
                                          </span>
                                        </div>
                                      ) : null}
                                      <div className={`p-3 rounded border ${
                                        isSkipped
                                          ? '!border-gray-300 dark:!border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                                          : filter.passed
                                          ? '!border-green-300 dark:!border-green-700 bg-green-100/70 dark:bg-green-900/30'
                                          : '!border-red-300 dark:!border-red-700 bg-red-100/70 dark:bg-red-900/30'
                                      }`}>
                                        {group.conditions.map((condition, condIndex) => {
                                          const isFailed = !condition.matched && filter.details?.failed_condition?.field === condition.field
                                          return (
                                            <div key={condIndex}>
                                              {/* Show "and" or "or" label between conditions within group */}
                                              {condIndex > 0 && (
                                                <div className="flex items-center justify-center my-2">
                                                  <span className="px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:!border-gray-700">
                                                    {group.operator}
                                                  </span>
                                                </div>
                                              )}
                                              <div
                                                className={`p-3 rounded border mb-2 ${
                                                  isSkipped
                                                    ? '!border-gray-300 dark:!border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                                                    : condition.matched
                                                    ? '!border-green-300 dark:!border-green-700 bg-green-100/70 dark:bg-green-900/30'
                                                    : '!border-red-300 dark:!border-red-700 bg-red-100/70 dark:bg-red-900/30'
                                                }`}
                                              >
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                                  <div className="flex items-start gap-2 flex-1 min-w-0">
                                                    {isSkipped ? (
                                                      <CircleMinus className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                                                    ) : condition.matched ? (
                                                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                                    ) : (
                                                      <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                      <p className="text-sm font-medium text-blue-950 dark:text-white">
                                                        {condition.field} {getOperatorLabel(condition.operator)} "{condition.expected_value}"
                                                      </p>
                                                      {!isSkipped && (
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                          Actual value: {formatActualValue(condition.actual_value)}
                                                        </p>
                                                      )}
                                                    </div>
                                                  </div>
                                                  {!isSkipped && (
                                                    <TagLabel
                                                      textColor={condition.matched ? "text-green-600 dark:text-green-600" : "text-red-600 dark:text-red-600"}
                                                      className={`inline-flex border px-1.5 py-1 rounded-sm bg-transparent ${condition.matched ? "!border-green-600 dark:!border-green-600" : "!border-red-600 dark:!border-red-600"}`}
                                                      leftIcon={condition.matched ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                                    >
                                                      {condition.matched ? 'Condition matched' : 'Condition did not match'}
                                                    </TagLabel>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      {/* Actions Executed */}
                      {isSuccess ? (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Zap className="w-4 h-4 text-gray-500 hidden sm:block" />
                            <h4 className="font-medium text-blue-950 dark:text-white">
                              Actions Executed ({result.actions.length})
                            </h4>
                          </div>
                          <div className="ml-0 sm:ml-6 space-y-2">
                            {result.actions.length > 0 ? (
                              result.actions
                                .sort((a, b) => a.action_order - b.action_order)
                                .map((action) => (
                                  <div
                                    key={action.action_id}
                                    className="p-3 rounded border !border-blue-200 dark:!border-blue-800 bg-blue-50 dark:bg-blue-900/20"
                                  >
                                    <div className="flex items-start gap-2">
                                      {action.success ? (
                                        <CheckCircle2 className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                      ) : (
                                        <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                      )}
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-blue-950 dark:text-white">
                                          {getActionTypeLabel(action.action_type)}
                                        </p>
                                        {action.details && Object.keys(action.details).length > 0 && (
                                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {Object.entries(action.details)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(', ')}
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                          Step {action.action_order} • Executed{' '}
                                          {formatDistanceToNow(new Date(action.executed_at), {
                                            addSuffix: true,
                                          })}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                No actions were executed
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Zap className="w-4 h-4 text-gray-500 hidden sm:block" />
                            <h4 className="font-medium text-blue-950 dark:text-white">
                              Actions Executed
                            </h4>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 ml-0 sm:ml-6">
                            {(() => {
                              const failedFilter = result.filters.find(f => !f.passed)
                              const filterName = failedFilter?.filter_name || `filter step ${result.filters.findIndex(f => !f.passed) + 1}`
                              return `Workflow failed at ${filterName}`
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      </div>

      {/* Block Dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block Sender</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to block {email.fromEmail}? Future emails from this sender
              will be automatically filtered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmToggleBlocked}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unblock Dialog */}
      <AlertDialog open={showUnblockDialog} onOpenChange={setShowUnblockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock Sender</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unblock {email.fromEmail}? Future emails from this sender
              will be processed normally.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmToggleBlocked}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
    </>
  )
}
